import {
  buildAccessibilityHint,
  type BuildAccessibilityHintOptions,
} from "./buildAccessibilityHint";
import {
  buildAccessibilityLabel,
  type BuildAccessibilityLabelOptions,
} from "./buildAccessibilityLabel";

import { Platform } from "react-native";

/**
 * Options for building all accessibility props for an input element.
 *
 * Options are grouped by concern:
 *
 * ## Label & State (accessible name)
 * - `label` - Field label text
 * - `required`, `requiredText`, `optionalText` - Required/optional state
 * - `invalid`, `invalidText` - Invalid state
 * - `disabled`, `disabledText` - Disabled state
 * - `value` - Current value (for button-style inputs)
 * - `labelSeparator` - Separator between label parts (defaults to ". ")
 *
 * ## Hint & Error (accessible description on native)
 * - `baseHint` - Base hint text
 * - `errorMessage`, `errorPrefix` - Error message and prefix
 * - `hintSeparator` - Separator between error and hint (defaults to " ")
 *
 * ## ID References (for web + Android external labels)
 * - `labelId` - External label element ID
 * - `errorMessageId` - Error message element ID
 * - `explainerTextId` - Explainer/helper text element ID
 * - `controlsId` - ID of the element controlled by this input (e.g. a listbox)
 * - `hasExternalLabel`, `hasPlaceholder` - Android workaround flags
 *
 * **Extension rule:** Only add options for core, reusable input semantics.
 * Product- or flow-specific behavior belongs in wrappers (e.g., Onboarding's
 * `buildLocalizedAccessibilityProps`).
 */
export interface BuildInputAccessibilityPropsOptions
  extends BuildAccessibilityLabelOptions, BuildAccessibilityHintOptions {
  /**
   * Separator for the hint (between error and base hint).
   * Defaults to " " (space) to match MADS behavior.
   * Onboarding uses ". " (period-space) for clarity.
   */
  hintSeparator?: string;

  /**
   * ID of the external label element for `accessibilityLabelledBy` (Android)
   * and `aria-labelledby` (web).
   */
  labelId?: string;

  /**
   * ID of the error message element for `aria-describedby` (web).
   */
  errorMessageId?: string;

  /**
   * ID of the explainer/helper text element for `aria-describedby` (web).
   */
  explainerTextId?: string;

  /**
   * ID of the element controlled by this input for `accessibilityControls` (native)
   * and `aria-controls` (web).
   */
  controlsId?: string;

  /**
   * ID of the element owned by this input for `aria-owns` (web).
   * Use when the owned element is in a portal outside the DOM subtree
   * (e.g. a listbox rendered via createPortal) so screen readers can
   * associate it with this input for aria-activedescendant to work.
   */
  ownsId?: string;

  /**
   * Whether the controlled region (e.g. listbox, combobox) is expanded.
   * Used for `aria-expanded` (web) on inputs that control expandable content.
   */
  expanded?: boolean;

  /**
   * Whether the editable `<TextInput>` currently has a non-empty value.
   * On Android, TalkBack reads the input's text value as the accessible name
   * and skips accessibilityLabel. When true on Android, the visible label
   * (with its required state) is mirrored into accessibilityHint so it stays
   * announced after the value.
   */
  hasValue?: boolean;
}

/**
 * Accessibility props returned for input elements.
 *
 * These props should be spread onto the input element. The utility returns
 * platform-appropriate props:
 *
 * - **Native iOS**: `accessibilityLabel` and `accessibilityHint`
 * - **Native Android**: `accessibilityLabel` or `accessibilityLabelledBy`, plus `accessibilityHint`
 * - **Web**: `aria-label`, `aria-labelledby`, `aria-required`, `aria-invalid`, `aria-describedby`
 *
 * Props that don't apply to the current platform are returned as `undefined`.
 */
export interface InputAccessibilityProps {
  /**
   * Native: Label with required/optional/invalid state appended.
   * Undefined on web (uses aria-label/aria-labelledby instead).
   */
  accessibilityLabel?: string;

  /**
   * Native: Hint with error message prepended.
   * Undefined on web (uses aria-describedby instead).
   */
  accessibilityHint?: string;

  /**
   * Native Android: ID of external label element when placeholder or external label is used.
   */
  accessibilityLabelledBy?: string;

  /**
   * Web: Accessible name when no external label is provided.
   */
  "aria-label"?: string;

  /**
   * Web: ID of external label element.
   */
  "aria-labelledby"?: string;

  /**
   * Web: True when required, false otherwise (matches original MADS behavior).
   */
  "aria-required"?: boolean;

  /**
   * Web: True when field is in invalid state.
   */
  "aria-invalid"?: boolean;

  /**
   * Web: True when field is disabled.
   */
  "aria-disabled"?: boolean;

  /**
   * Web: Space-separated IDs of error/explainer elements.
   */
  "aria-describedby"?: string;

  /**
   * Web: ID of controlled element.
   */
  "aria-controls"?: string;

  /**
   * Native: ID of controlled element.
   */
  accessibilityControls?: string;

  /**
   * Web: ID of owned element (for portaled content outside the DOM subtree).
   */
  "aria-owns"?: string;

  /**
   * Web: True when the controlled region is expanded (e.g. listbox open).
   */
  "aria-expanded"?: boolean;
}

/**
 * Builds all accessibility props needed for an input field across all platforms.
 *
 * This utility consolidates the logic for both native (iOS/Android) and web accessibility,
 * following the MADS TextInput patterns. It is designed to be i18n-agnostic - consumers
 * pass pre-translated strings.
 *
 * ## Platform-specific behavior
 *
 * **iOS:**
 * - `accessibilityLabel`: Label with required/optional/invalid states appended
 * - `accessibilityHint`: Hint with error message prepended
 *
 * **Android:**
 * - `accessibilityLabel`: Same as iOS, BUT returns undefined when placeholder or external label is used
 * - `accessibilityLabelledBy`: Set to `labelId` when placeholder or external label is used
 * - `accessibilityHint`: Hint with error message prepended
 *
 * **Web:**
 * - `aria-label`: Accessible name when no external label is provided
 * - `aria-labelledby`: ID of external label element when provided
 * - `aria-required`: True when field is required
 * - `aria-invalid`: True when field is invalid
 * - `aria-describedby`: Space-separated IDs of error and/or explainer elements
 *
 * ## Error Message Handling
 *
 * On native, the error message is prepended to the accessibility hint.
 * On web, the error message element is referenced via aria-describedby.
 *
 * The `errorPrefix` option allows adding a prefix like "Error: " before error messages.
 * This is recommended for clarity but not added by default for backward compatibility.
 *
 * @example
 * // Basic required field
 * buildInputAccessibilityProps({
 *   label: 'Email',
 *   required: true,
 * })
 *
 * // Field with error (MADS pattern - no prefix)
 * buildInputAccessibilityProps({
 *   label: 'Email',
 *   invalid: true,
 *   errorMessage: 'Email is required',
 *   errorMessageId: 'email-error',
 * })
 *
 * // Field with error (recommended - with prefix)
 * buildInputAccessibilityProps({
 *   label: 'Email',
 *   invalid: true,
 *   errorMessage: 'Email is required',
 *   errorPrefix: 'Error: ',
 *   errorMessageId: 'email-error',
 * })
 *
 * // Button-style input with value
 * buildInputAccessibilityProps({
 *   label: 'Country',
 *   value: 'USA',
 *   baseHint: 'Opens selection modal',
 * })
 */
export function buildInputAccessibilityProps({
  label,
  required,
  requiredText,
  optionalText,
  invalid,
  invalidText,
  disabled,
  hasExternalLabel,
  hasPlaceholder,
  hasValue,
  value,
  labelSeparator,
  errorMessage,
  errorPrefix,
  baseHint,
  hintSeparator,
  labelId,
  errorMessageId,
  explainerTextId,
  controlsId,
  ownsId,
  expanded,
}: BuildInputAccessibilityPropsOptions): InputAccessibilityProps {
  const isWeb = Platform.OS === "web";
  const isAndroid = Platform.OS === "android";

  // Build native accessibility label (undefined on web)
  const accessibilityLabel = buildAccessibilityLabel({
    label,
    required,
    requiredText,
    optionalText,
    invalid,
    invalidText,
    disabled,
    hasExternalLabel,
    hasPlaceholder,
    value,
    labelSeparator,
  });

  // Android: TalkBack skips accessibilityLabel once a value is bound to the input,
  // so mirror the label with required state into the hint.
  if (isAndroid && hasValue && label) {
    const labelForHint = buildAccessibilityLabel({
      label,
      required,
      requiredText,
    });
    if (labelForHint) {
      baseHint = baseHint ? `${labelForHint}. ${baseHint}` : labelForHint;
    }
  }

  // Build native accessibility hint (undefined on web)
  const accessibilityHint = buildAccessibilityHint({
    errorMessage,
    errorPrefix,
    baseHint,
    separator: hintSeparator,
  });

  // Android: Use accessibilityLabelledBy when placeholder or external label is present
  // This works around the TalkBack bug where placeholder overrides accessibilityLabel
  const accessibilityLabelledBy =
    isAndroid && (hasPlaceholder || hasExternalLabel) && labelId ? labelId : undefined;

  // Web: aria-label for accessible name when no external label
  // Note: comma separator is intentionally hardcoded as this is for web screen readers,
  // not user-facing translated content. Matches original MADS TextInput behavior.
  let ariaLabel: string | undefined;
  if (isWeb && !hasExternalLabel && !labelId) {
    const trimmedLabel = label?.trim();
    const trimmedValue = value?.trim();

    if (trimmedLabel && trimmedValue) {
      ariaLabel = `${trimmedLabel}, ${trimmedValue}`;
    } else {
      ariaLabel = trimmedLabel || trimmedValue;
    }
  }

  // Web: aria-labelledby when external label is used
  const ariaLabelledBy = isWeb && labelId ? labelId : undefined;

  // Web: aria-required - original MADS explicitly set false for non-required fields
  const ariaRequired = isWeb ? !!required : undefined;

  // Web: aria-invalid (only set true, don't set false)
  const ariaInvalid = isWeb && invalid ? true : undefined;

  // Web: aria-disabled
  const ariaDisabled = isWeb && disabled ? true : undefined;

  // Web: aria-controls
  const ariaControls = isWeb && controlsId ? controlsId : undefined;

  // Web: aria-owns (for portaled owned elements outside the DOM subtree)
  const ariaOwns = isWeb && ownsId ? ownsId : undefined;

  // aria-expanded: pass through as-is. When not provided (undefined),
  // screen readers won't announce "Collapsed" on non-expandable inputs.
  const ariaExpanded = expanded;

  // Native: accessibilityControls
  const accessibilityControls = !isWeb && controlsId ? controlsId : undefined;

  // Web: aria-describedby - combine error and explainer IDs
  let ariaDescribedBy: string | undefined;
  if (isWeb) {
    const ids: string[] = [];
    if (invalid && errorMessageId) {
      ids.push(errorMessageId);
    }
    if (explainerTextId) {
      ids.push(explainerTextId);
    }
    if (ids.length > 0) {
      ariaDescribedBy = ids.join(" ");
    }
  }

  return {
    accessibilityLabel,
    accessibilityHint,
    accessibilityLabelledBy,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-required": ariaRequired,
    "aria-invalid": ariaInvalid,
    "aria-disabled": ariaDisabled,
    "aria-describedby": ariaDescribedBy,
    "aria-controls": ariaControls,
    accessibilityControls,
    "aria-owns": ariaOwns,
    "aria-expanded": ariaExpanded,
  };
}
